import { config } from '@/config/config'

/**
 * Format distance in meters to human-readable string
 */
export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)} m`
  }
  return `${(meters / 1000).toFixed(1)} km`
}

/**
 * Format time in minutes to human-readable string
 */
export function formatTime(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`
  }
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (mins === 0) {
    return `${hours} hr`
  }
  return `${hours} hr ${mins} min`
}

/**
 * Format time range
 */
export function formatTimeRange(min: number, max: number): string {
  return `${min}-${max} min`
}

/**
 * Format currency
 */
export function formatCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`
}

/**
 * Format delivery fee range
 */
export function formatDeliveryFee(min: number, max: number): string {
  if (min === 0 && max === 0) {
    return 'Free delivery'
  }
  if (min === max) {
    return formatCurrency(min)
  }
  return `${formatCurrency(min)} - ${formatCurrency(max)}`
}

/**
 * Format store status for display
 */
export function formatStatus(status: string): string {
  const statusMap: Record<string, string> = {
    open: 'Open',
    closing_soon: 'Closing soon',
    closed: 'Closed',
    opens_soon: 'Opens soon',
  }
  return statusMap[status] || status
}

/**
 * Format address for single-line display
 */
export function formatAddressLine(
  street: string,
  suburb: string,
  state: string,
  postcode: string
): string {
  return `${street}, ${suburb} ${state} ${postcode}`
}

/**
 * Build the full store photo URL from the storePhotoPath field.
 * Falls back to logoPath if storePhotoPath is not available.
 * Returns null when neither path is available.
 */
export function getStorePhotoUrl(storePhotoPath?: string, logoPath?: string): string | null {
  const pathToUse = storePhotoPath || logoPath
  if (!pathToUse) return null
  const base = config.api.storageBaseUrl.replace(/\/+$/, '')
  const path = pathToUse.startsWith('/') ? pathToUse : `/${pathToUse}`
  return `${base}${path}`
}
